import { IActionTrigger, Label } from "../../../resolvers-types";
import { INGAudioPlayerProps } from "../../library/NGFieldExtensions";
import "./NGAudioPlayer.css";

import { useSignal } from "@preact/signals-react";
import { SyntheticEvent, useEffect, useRef } from "react";
import { publishUserAction } from "../../library/analytics";
import { FormContextType, setupHandlers, setupLocalState } from "../../library/dataService";
import { log } from "../../library/logger";
import { getFilesNatsBaseUrl, getSupportedPlaybackAudioMimeType, getTestId, isNullOrEmpty } from "../../library/utils";
import NGLabel from "../NGLabel/NGLabel";
import NGSwitch from "../NGSwitch/NGSwitch";

const tag = "NGAudioPlayer";

export default function NGAudioPlayer({ config, context }: INGAudioPlayerProps) {
  const local = setupLocalState(
    config,
    {
      Value: useSignal(config.Value ?? ""),
      DestinationBucket: useSignal(config.DestinationBucket ?? null),
      Text: useSignal(config.Text ?? ""),
      Visible: useSignal(config.Visible ?? true),
    },
    context
  );

  log.info(tag, `render ${local.Value.value} `, config);

  // getFilesNatsBaseUrl
  const handlers = setupHandlers(config, context);

  const url = useSignal("");

  const showTranscript = useSignal(false);

  useEffect(() => {
    if (!local.Visible.value || !local.Value.value) return;
    const value = local.Value.value;

    if (value.startsWith("http")) {
      url.value = value;
    } else {
      let v = getFilesNatsBaseUrl();

      if (local.DestinationBucket.value) {
        v += local.DestinationBucket.value + "/";
      }

      url.value = v + local.Value.value;

      const currentExtension = url.value.split(".").pop();
      log.info(tag, "currentExtension", currentExtension);

      const recordingExtension = "mp3";
      log.info(tag, "recordingExtension", recordingExtension);
      if (recordingExtension !== currentExtension) {
        url.value = url.value.replace("." + currentExtension, "." + recordingExtension);
        log.info(tag, "~~post replacement url.value", url.value);
      }
      log.info(tag, "~~final url.value", url.value);
    }

    if (audioRef.current) {
      (audioRef.current as any).load();
    }
  }, [local.Visible.value, local.Value.value, url, local.DestinationBucket.value]);

  const audioRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (audioRef.current) {
      (audioRef.current as any).load();
    }
  }, []);

  // analytics
  useEffect(() => {
    if (!audioRef.current) return;

    const player = audioRef.current;

    const handlePlay = () => {
      publishUserAction(config, context, "media_play", null, {
        EventCategory: "content",
        MediaType: config.RenderAsVideoPlayer ? "video" : "audio",
        MediaUrl: url.value,
      });
    };

    const handlePause = () => {
      publishUserAction(config, context, "media_pause", null, {
        EventCategory: "content",
        MediaType: config.RenderAsVideoPlayer ? "video" : "audio",
        MediaUrl: url.value,
        CurrentTime: player.currentTime,
      });
    };

    const handleEnded = () => {
      publishUserAction(config, context, "media_complete", null, {
        EventCategory: "content",
        MediaType: config.RenderAsVideoPlayer ? "video" : "audio",
        MediaUrl: url.value,
      });
    };

    const handleTimeUpdate = () => {
      if (player.currentTime % 30 < 1) {
        // Every ~30 seconds
        publishUserAction(config, context, "media_progress", null, {
          EventCategory: "content",
          MediaType: config.RenderAsVideoPlayer ? "video" : "audio",
          MediaUrl: url.value,
          CurrentTime: Math.floor(player.currentTime),
          DurationPercentage: Math.floor((player.currentTime / player.duration) * 100),
        });
      }
    };

    // Add event listeners
    player.addEventListener("play", handlePlay);
    player.addEventListener("pause", handlePause);
    player.addEventListener("ended", handleEnded);
    player.addEventListener("timeupdate", handleTimeUpdate);

    // Cleanup
    return () => {
      player.removeEventListener("play", handlePlay);
      player.removeEventListener("pause", handlePause);
      player.removeEventListener("ended", handleEnded);
      player.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [config, context, url.value]);

  const audioType = getSupportedPlaybackAudioMimeType(url.value);

  return (
    <>
      {local.Visible.value && (
        <div className="audioPlayer">
          {config.RenderAsVideoPlayer ? (
            <video ref={audioRef} controls className="audioControl" preload="metadata">
              <source src={url.value} type={audioType as any} data-testid={getTestId(config)} />
            </video>
          ) : (
            <audio ref={audioRef} controls className="audioControl" preload="metadata">
              <source src={url.value} type={audioType as any} data-testid={getTestId(config)} />
            </audio>
          )}
          {!isNullOrEmpty(local.Text.value) && (
            <>
              <div className="transcriptSwitch">
                <NGSwitch
                  config={{
                    Id: `tr_${config.Id}`,
                    LabelWhenOff: "Show Transcript",
                    LabelWhenOn: "Hide Transcript",
                    //Color: "#0c76bd",
                    DefaultValue: showTranscript.value.toString(),
                    LabelStyle: {
                      fontSize: "0.8rem",
                      fontWeight: "semi-bold",
                    },
                    Actions: [
                      {
                        Trigger: "onChange",
                        preHandler: (
                          handlerName: string,
                          action: IActionTrigger,
                          e: SyntheticEvent,
                          data: object,
                          formCtx: FormContextType
                        ) => {
                          console.log("onChange", e, data, formCtx);
                          showTranscript.value = (data as unknown) === true;
                        },
                      },
                    ],
                  }}
                  layoutType={""}
                  context={context}
                />
              </div>
              {showTranscript.value && (
                <NGLabel
                  config={
                    {
                      Id: `lb_${config.Id}`,
                      Value: local.Text.value,
                      MultiLine: true,
                      Style: config.TranscriptStyle,
                    } as Label
                  }
                  context={context}
                />
                // <TextareaAutosize
                //   className="transcriptText"
                //   value={local.Text.value}
                //   disabled={true}
                // ></TextareaAutosize>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
